import { err, ok, type Result } from '@shared/result';
import { PersonName, type PersonNameError } from './PersonName';

/**
 * El nombre de una persona del padrón, capturado en las cuatro partes con que se
 * escribe en México: primer nombre, segundo nombre, primer apellido, segundo
 * apellido.
 *
 * **Por qué cuatro campos y no uno.** El alta la captura un administrador sobre
 * un documento de RR.HH., y el nombre compuesto de una sola caja no se puede
 * volver a partir después: «José Carlos Lugardo Ramírez» y «María de los Ángeles
 * Ríos» tienen lecturas distintas, y ninguna heurística acierta las dos. Partirlo
 * al capturar es gratis; partirlo después es adivinar. Por eso el alta pide las
 * partes y el sistema arma el nombre, y no al revés.
 *
 * **Y por qué el compuesto sigue mandando.** `Person.displayName` es lo que leen
 * las solicitudes, el calendario, las aprobaciones y los correos, desnormalizado
 * en varias colecciones para evitar N+1. Estas cuatro partes son la **entrada**;
 * `displayName` sigue siendo la única cadena que alguien muestra. Un registro
 * viejo tiene `displayName` y no tiene partes, y eso es válido para siempre — ver
 * `Person.name`.
 *
 * **Cada parte admite varias palabras, y es obligatorio que así sea.** «de la
 * Cruz», «del Río» y «Ponce de León» son un apellido, no tres; exigir una palabra
 * por casilla dejaría fuera a una parte grande del país. Lo que no se admite son
 * dígitos ni signos, que es donde está la diferencia entre un nombre y un tecleo.
 *
 * La comprobación final la hace `PersonName`, que ya sostiene esa regla para los
 * contactos de emergencia. Reutilizarla en vez de copiarla es lo que garantiza
 * que un nombre armado aquí cumpla exactamente lo mismo que cualquier otro nombre
 * del sistema — incluido su tope de longitud, que aquí es fácil de rebasar
 * sumando cuatro partes.
 */

/** Cuál de las cuatro casillas falló. Un mensaje sin esto no dice dónde tocar. */
export type NamePart =
  | 'primerNombre'
  | 'segundoNombre'
  | 'primerApellido'
  | 'segundoApellido';

export type PersonFullNameError =
  | { readonly kind: 'required'; readonly part: NamePart }
  | { readonly kind: 'invalid-characters'; readonly part: NamePart; readonly raw: string }
  /** El compuesto no pasó la misma regla que cualquier otro nombre del sistema. */
  | { readonly kind: 'composed'; readonly cause: PersonNameError };

export interface PersonFullNameParts {
  readonly primerNombre: string;
  readonly segundoNombre?: string | undefined;
  readonly primerApellido: string;
  readonly segundoApellido?: string | undefined;
}

/**
 * Una parte: una o más palabras, cada una empezando por letra y siguiendo con
 * letras, marcas de acento, apóstrofo, guion o punto.
 *
 * `\p{M}` está por los acentos que se escriben como carácter aparte, que es lo
 * que produce un teclado de macOS al componer — sin él, «Ramírez» tecleado con
 * `alt+e` se rechaza y la pantalla acusa a quien escribió bien.
 */
const PART = /^\p{L}[\p{L}\p{M}'’.-]*(?: \p{L}[\p{L}\p{M}'’.-]*)*$/u;

const normalize = (raw: string): string => raw.trim().replace(/\s+/g, ' ');

/**
 * Las partículas que van en minúscula dentro de un apellido español.
 *
 * «de la Cruz», «del Río», «Ponce de León». Capitalizarlas produce «De La Cruz»,
 * que no es cómo se escribe el nombre de nadie — y es exactamente lo que hace un
 * *title case* ingenuo.
 */
const PARTICLES = new Set([
  'de', 'del', 'la', 'las', 'lo', 'los', 'y', 'e', 'da', 'das', 'do', 'dos',
  'van', 'von', 'der', 'di', 'du', 'le',
]);

/**
 * Capitaliza una palabra **sin destruir la que ya venía bien escrita.**
 *
 * La regla es: si la palabra ya lleva una mayúscula interior, quien la escribió
 * lo hizo a propósito y se respeta. «McDonald» y «DeLeón» sobreviven; «LUGARDO»
 * —lo que sale de casi cualquier hoja de cálculo de RR.HH.— y «lugardo» se
 * normalizan a «Lugardo».
 *
 * Sin esa excepción habría que elegir entre arreglar los ALL CAPS y conservar
 * «McDonald», y las dos pérdidas son reales. Mirar si hay mayúscula interior las
 * distingue sin preguntar.
 */
const capitalizeWord = (word: string): string => {
  const rest = word.slice(1);
  if (/\p{Lu}/u.test(rest) && /\p{Ll}/u.test(word)) return word;

  /*
   * Se capitaliza también tras apóstrofo, guion y punto: «D'Angelo»,
   * «Ana-María», «Ma. Guadalupe». Son separadores dentro de una palabra, y
   * `\b` de JavaScript no los trata igual en todos los casos con Unicode, así
   * que el corte se hace explícito.
   */
  return word
    .toLocaleLowerCase('es')
    .replace(/(^|['’.-])(\p{L})/gu, (_, sep: string, letter: string) =>
      `${sep}${letter.toLocaleUpperCase('es')}`,
    );
};

/**
 * Capitaliza una parte del nombre respetando las partículas.
 *
 * Una partícula que es la **única** palabra de la casilla sí se capitaliza: si
 * alguien escribió «De» como primer apellido, es su apellido entero y dejarlo en
 * minúscula lo haría ver como un error de captura.
 *
 * **Exportada porque el formulario la aplica en cada tecla.** Es la misma función
 * que corre al guardar, y tiene que serlo: dos implementaciones de «cómo se
 * capitaliza un apellido» acabarían discrepando, y la discrepancia se vería como
 * un campo que enseña una cosa y guarda otra.
 *
 * **No cambia la longitud de la cadena**, sólo la caja de sus letras. De eso
 * depende que el formulario pueda devolver el cursor a su sitio después de
 * reescribir el campo: el índice del cursor sigue siendo válido.
 */
export const capitalizeNamePart = (part: string): string => {
  const words = part.split(' ');
  return words
    .map((word) =>
      words.length > 1 && PARTICLES.has(word.toLocaleLowerCase('es'))
        ? word.toLocaleLowerCase('es')
        : capitalizeWord(word),
    )
    .join(' ');
};

/** Las dos que no pueden faltar. Un segundo nombre o un segundo apellido sí. */
const REQUIRED: readonly NamePart[] = ['primerNombre', 'primerApellido'];

export class PersonFullName {
  private constructor(
    readonly primerNombre: string,
    readonly segundoNombre: string | null,
    readonly primerApellido: string,
    readonly segundoApellido: string | null,
    /** El compuesto, ya validado. Esto es lo que va a `displayName`. */
    readonly full: PersonName,
  ) {}

  static create(
    parts: PersonFullNameParts,
  ): Result<PersonFullName, PersonFullNameError> {
    const clean: Record<NamePart, string> = {
      primerNombre: normalize(parts.primerNombre),
      segundoNombre: normalize(parts.segundoNombre ?? ''),
      primerApellido: normalize(parts.primerApellido),
      segundoApellido: normalize(parts.segundoApellido ?? ''),
    };

    for (const part of REQUIRED) {
      if (clean[part].length === 0) return err({ kind: 'required', part });
    }

    /*
     * El orden importa: se revisa cada casilla antes de componer, para que el
     * error señale «Primer apellido» y no «el nombre completo». Un formulario que
     * sabe cuál de sus cuatro campos está mal y no lo dice obliga a adivinar.
     */
    for (const part of Object.keys(clean) as NamePart[]) {
      const value = clean[part];
      if (value.length > 0 && !PART.test(value)) {
        return err({ kind: 'invalid-characters', part, raw: value });
      }
    }

    /*
     * Se capitaliza después de validar y antes de componer, así que lo que se
     * guarda en las cuatro casillas y lo que se guarda en `displayName` no pueden
     * discrepar: salen de la misma cadena.
     */
    for (const part of Object.keys(clean) as NamePart[]) {
      clean[part] = capitalizeNamePart(clean[part]);
    }

    // El orden de lectura en México: nombres primero, apellidos después.
    const composed = [
      clean.primerNombre,
      clean.segundoNombre,
      clean.primerApellido,
      clean.segundoApellido,
    ]
      .filter((piece) => piece.length > 0)
      .join(' ');

    const full = PersonName.create(composed);
    if (!full.ok) return err({ kind: 'composed', cause: full.error });

    return ok(
      new PersonFullName(
        clean.primerNombre,
        clean.segundoNombre.length > 0 ? clean.segundoNombre : null,
        clean.primerApellido,
        clean.segundoApellido.length > 0 ? clean.segundoApellido : null,
        full.value,
      ),
    );
  }

  /** Lo que se guarda en `Person.displayName`. */
  get displayName(): string {
    return this.full.value;
  }
}

/** La etiqueta de cada casilla, para que el mensaje nombre lo que el ojo ve. */
export const NAME_PART_LABEL: Readonly<Record<NamePart, string>> = {
  primerNombre: 'Primer nombre',
  segundoNombre: 'Segundo nombre',
  primerApellido: 'Primer apellido',
  segundoApellido: 'Segundo apellido',
};
